// Pure helpers behind the "ดึงสินค้าจาก Backoffice" picker on
// /seller/wms-products (src/views/AppCatalog/WmsProductList.vue).
//
// WHY THE MODAL CHANGED (owner, 2026-10-03). The first cut of the picker only
// ever showed SEARCH RESULTS: it opened empty and staff had to guess a word
// before anything appeared. On a warehouse of a few thousand products that
// reads as "there is nothing to import". So the modal now loads page 1 of the
// whole Backoffice warehouse list on open, and the search box NARROWS that same
// list instead of being the only way into it. Clearing the box is a search for
// "" — it must bring everything back, not leave the table on the last match.
//
// --------------------------------------------------------------------------
// BACKEND CONTRACT (mdf-bo-api, parallel PR):
//
//   GET /api/v1/bo/catalog/wms-products?q=&page=&page_size=
//   200 {"data":[{"id":1,"name":"...","barcode":"...","image":"...",
//                 "man_pn":"SHINE50","cost":12.5,
//                 "price": 199 | null,     // null = no price set in BO
//                 "imported": true}],
//        "total":1234,"page":1,"page_size":50}
//
// The SAME endpoint still answers the older `?q=&limit=` form with a body that
// has NO `total` (that is what the bundle picker in PromotionBundleEdit.vue
// sends, through its own resource — see wmsPickerTotal's fallback). Both forms
// have to keep working, so nothing here assumes `total` is present and nothing
// here rewrites `limit`.
// --------------------------------------------------------------------------
//
// Vue-free / axios-free on purpose: this repo's jest cannot mount a .vue file
// (reference-bo-fn-jest-infra, see tests/unit/skuWmsOptionsSource.spec.js), so
// every rule worth testing lives here and the dialog only renders it. Behaviour
// is specified in tests/unit/wmsImportModal.spec.js.

/** The picker's page size. One place, so the request and the <el-pagination> agree. */
export const WMS_PICKER_PAGE_SIZE = 50;

/**
 * The biggest page the backend honours. Above this it SILENTLY falls back to 50,
 * so an un-clamped page_size would have <el-pagination> stride by (say) 500 over
 * pages that are 50 rows long — every page after the first showing the wrong
 * rows, with nothing in the UI to say why.
 */
export const WMS_PICKER_MAX_PAGE_SIZE = 100;

/** Positive integer or the given fallback. Rejects 0, NaN, "", null, "abc", -3. */
function toPositiveInt(v, fallback) {
  const n = Number(v);
  if (!isFinite(n) || n < 1) return fallback;
  return Math.floor(n);
}

/**
 * wmsPickerParams({ q, page, pageSize }) — the query string for one page.
 *
 * Always sends `page` and `page_size` (snake_case: that is what the handler
 * binds) and NEVER sends `limit` — mixing the two forms in one request is how a
 * paged list silently collapses to the first 50 rows forever.
 *
 * `q` is trimmed and always present, including as "": the empty search is a
 * real request here ("show me everything again" after the box is cleared), so
 * dropping the key would make "cleared" indistinguishable from "unchanged" to
 * anything that diffs the params.
 */
export function wmsPickerParams(opts) {
  const o = opts || {};
  const q = (o.q === null || o.q === undefined) ? '' : String(o.q).trim();
  return {
    q,
    page: toPositiveInt(o.page, 1),
    page_size: Math.min(
      toPositiveInt(o.pageSize, WMS_PICKER_PAGE_SIZE),
      WMS_PICKER_MAX_PAGE_SIZE
    ),
  };
}

/**
 * formatWmsPrice(v) — the ราคา cell.
 *
 * "-" when the Backoffice has no price (null / undefined / "" / not a number),
 * otherwise two decimals with thousands separators. The dash matters: a
 * missing price rendered as "0.00" tells staff the product is free.
 *
 * ONLY a number or a non-blank string is a price. Everything else is rejected
 * BEFORE Number() gets a look, because Number() answers 0 for "", "  ", null,
 * [], false and {valueOf:...} — each of which would print "0.00". A whitespace
 * cell out of a CSV-ish BE field is the realistic one.
 *
 * Grouping is done by hand rather than with toLocaleString so the output does
 * not depend on the browser's (or Node's) ICU locale.
 */
export function formatWmsPrice(v) {
  if (typeof v !== 'number' && (typeof v !== 'string' || v.trim() === '')) return '-';
  const n = Number(v);
  if (!isFinite(n)) return '-';
  const fixed = Math.abs(n).toFixed(2);
  const parts = fixed.split('.');
  const grouped = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (n < 0 ? '-' : '') + grouped + '.' + parts[1];
}

/**
 * wmsRowSelectable(row) — el-table's `:selectable`.
 *
 * Already-imported rows cannot be ticked. The import endpoint is an idempotent
 * upsert, so re-importing is harmless to the data — but a checkbox that can be
 * ticked implies the row is not in the catalog yet, and staff were using the
 * tick to decide what still had to be brought over.
 *
 * Truthy `imported` (true, 1, "true") blocks selection; a row that does not
 * carry the field at all is selectable, which is what the older API's rows look
 * like.
 */
export function wmsRowSelectable(row) {
  if (!row) return false;
  const v = row.imported;
  if (v === true || v === 1 || v === '1' || v === 'true') return false;
  return true;
}

/** The rows out of either body shape ({data:[...]} or a bare array). */
export function wmsPickerRows(res) {
  if (!res) return [];
  const body = (res.data && !Array.isArray(res.data)) ? res.data : res;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  return [];
}

/**
 * wmsPickerTotal(res) — the row count for <el-pagination>.
 *
 * Accepts the axios response, the body, or the body's `data` array, because
 * this is called from the store action (which holds the response) and from the
 * specs (which hold a body).
 *
 * FALLBACK TO rows.length when there is no `total`: the old `?limit=` form of
 * the endpoint answers without one, and a pagination bar told total=0 renders
 * as "no rows" over a table that plainly has rows. One page's worth is wrong by
 * however many pages follow, but it is never wrong about whether anything is
 * there.
 */
export function wmsPickerTotal(res) {
  if (!res) return 0;
  const body = (res.data && !Array.isArray(res.data)) ? res.data : res;
  const raw = body ? body.total : undefined;
  if (raw !== null && raw !== undefined && raw !== '') {
    const n = Number(raw);
    if (isFinite(n) && n >= 0) return Math.floor(n);
  }
  return wmsPickerRows(res).length;
}

export default {
  WMS_PICKER_PAGE_SIZE,
  wmsPickerParams,
  formatWmsPrice,
  wmsRowSelectable,
  wmsPickerRows,
  wmsPickerTotal,
};
